import { HttpLink } from '@apollo/client';
import { setContext } from '@apollo/client/link/context';
import * as prismic from '@prismicio/client';

const PRISMIC_ENDPOINT_REG =
  /^https?:\/\/([^.]+)\.(?:cdn\.)?(wroom\.(?:test|io)|prismic\.io)\/graphql\/?/;

function removeWhiteSpace(str: string) {
  const regexp = /(%0A|%20)*(%20|%7B|%7D)(%0A|%20)*/g;

  const [path, query] = str.split('?');
  if (!query) return str;

  const shortQuery = query
    .split('&')
    .map((param) => {
      const [name, value] = param.split('=');
      if (name === 'query') {
        return (
          name +
          '=' +
          value.replace(regexp, (chars, spaces, brackets) => brackets)
        );
      }
      return param;
    })
    .join('&');

  return [path, shortQuery].join('?');
}

function parsePrismicEndpoint(endpoint: string) {
  const tokens = endpoint.match(PRISMIC_ENDPOINT_REG);

  if (tokens !== null && Array.isArray(tokens) && tokens.length === 3) {
    const [, /* endpoint */ repository, domain] = tokens;

    return `https://${repository}.cdn.${domain}`; // enforce the cdn
  }

  return null; // not from prismic ? returns null.
}

interface PrismicLinkProps {
  uri: string;
  accessToken?: string;
  repositoryName?: string;
  [key: string]: any;
}

export function PrismicHttpLink({
  uri,
  accessToken,
  repositoryName,
  ...options
}: PrismicLinkProps) {
  const prismicEndpoint = parsePrismicEndpoint(uri); // enforce cdn if it's the prismic endpoint

  let apiEndpoint;
  let gqlEndpoint;

  if (prismicEndpoint && repositoryName) {
    apiEndpoint = prismic.getEndpoint(repositoryName);
    gqlEndpoint = `${prismicEndpoint}/graphql`;
  } else {
    apiEndpoint = `https://${repositoryName}.cdn.prismic.io/api/v2`;
    gqlEndpoint = uri;
  }

  const prismicClient = prismic.createClient(apiEndpoint, {
    accessToken,
  });

  const prismicLink = setContext((request, previousContext) => {
    return prismicClient.getRepository().then((repo) => {
      return {
        headers: {
          'Prismic-ref': repo.refs.find((x) => x.isMasterRef)?.ref,
          ...previousContext.headers,
          ...(repo.integrationFieldsRef
            ? {
                'Prismic-integration-field-ref': repo.integrationFieldsRef,
              }
            : {}),
          ...(accessToken ? { Authorization: `Token ${accessToken}` } : {}),
        },
      };
    });
  });

  const httpLink = new HttpLink({
    uri: gqlEndpoint,
    useGETForQueries: true,
    fetch: (url, options) => {
      const trimmed = removeWhiteSpace(url.toString());
      return fetch(trimmed, options);
    },
    ...options,
  });

  return prismicLink.concat(httpLink);
}
